
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import enTranslations from '@/i18n/en';
// Ensure translations are properly loaded

type Language = 'en' | 'es';

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType>({
  language: 'en',
  setLanguage: () => {},
  t: (key: string) => key,
});

// Cache for loaded translations
const translationCache: Record<string, Record<string, string>> = {
  en: enTranslations,
};

async function loadTranslations(lang: Language): Promise<Record<string, string>> {
  if (translationCache[lang]) return translationCache[lang];
  
  if (lang === 'es') {
    const mod = await import('@/i18n/es');
    translationCache[lang] = mod.default;
    return mod.default;
  }
  
  return enTranslations;
}

export const tGlobal = (key: string): string => {
  try {
    const saved = typeof window !== 'undefined' ? (localStorage.getItem('language') as Language) : 'en';
    const lang = saved && ['en','es'].includes(saved) ? saved : 'en';
    const current = translationCache[lang] || translationCache.en;
    return current[key] || translationCache.en[key] || key;
  } catch {
    return key;
  }
};

interface LanguageProviderProps {
  children: React.ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window !== 'undefined') {
      const savedLanguage = localStorage.getItem('language') as Language;
      return savedLanguage && ['en', 'es'].includes(savedLanguage) ? savedLanguage : 'en';
    }
    return 'en';
  });

  const [translations, setTranslations] = useState<Record<string, string>>(enTranslations);

  // Load translations when language changes
  useEffect(() => {
    let cancelled = false;
    loadTranslations(language).then(t => {
      if (!cancelled) setTranslations(t);
    });
    return () => { cancelled = true; };
  }, [language]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('language', language);
      document.documentElement.lang = language;
    }
  }, [language]);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
  }, []);

  const t = useCallback((key: string): string => {
    try {
      const translation = translations[key];
      if (translation === undefined || translation === null || translation === '') {
        return enTranslations[key] || key;
      }
      return String(translation);
    } catch (error) {
      console.error('Translation error:', error);
      return key;
    }
  }, [translations]);

  const contextValue: LanguageContextType = {
    language,
    setLanguage,
    t
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
